import firebase from "firebase/app";
import "firebase/functions";
import Fire from "./fire";

const fire = new Fire();
const firestore = fire.firestore;

export interface RawMyRoutineUser {
  id: string;
  name?: string;
  email: string;
  phone: string;
  selections: string[];
  createdAt: number;
  updatedAt: number;
  problem?: string;
  solvedBy?: string;
  isSolved?: boolean;
}

export interface RawMyRoutineUserWithLastDoc {
  users: RawMyRoutineUser[];
  lastDocOfNext: firebase.firestore.QueryDocumentSnapshot;
  lastDocOfPrevious: firebase.firestore.QueryDocumentSnapshot;
}

const COLLECTION = "quiz_results_from_customers";
export const FETCH_USERS_LIMIT = 7;

export default class MyRoutineFirestore {
  private static getQuizResultsRef(): firebase.firestore.CollectionReference {
    return firestore.collection(COLLECTION);
  }

  public static async fetchNextUsers(
    isSolved: boolean,
    startAfterDoc:
      | firebase.firestore.QueryDocumentSnapshot
      | undefined = undefined
  ): Promise<RawMyRoutineUserWithLastDoc> {
    const ref = this.getFetchNextRef(isSolved, startAfterDoc);
    const querySnapshot = await ref.get();
    const docs = await querySnapshot.docs;
    const users: RawMyRoutineUser[] = docs.map(doc => {
      const data = doc.data() as RawMyRoutineUser;
      data.id = doc.id;
      return data;
    });

    const lastDocOfPrevious = docs[0];
    const lastDocOfNext = docs[docs.length - 1];

    return {
      lastDocOfNext,
      lastDocOfPrevious,
      users,
    };
  }

  private static getFetchNextRef(
    isSolved: boolean,
    startAfterDoc:
      | firebase.firestore.QueryDocumentSnapshot
      | undefined = undefined
  ) {
    if (startAfterDoc) {
      return this.getQuizResultsRef()
        .where("isSolved", "==", isSolved)
        .orderBy("updatedAt", "desc")
        .startAfter(startAfterDoc)
        .limit(FETCH_USERS_LIMIT);
    }

    return this.getQuizResultsRef()
      .where("isSolved", "==", isSolved)
      .orderBy("updatedAt", "desc")
      .limit(FETCH_USERS_LIMIT);
  }

  public static async fetchPreviousUsers(
    isSolved: boolean,
    startAfterDoc:
      | firebase.firestore.QueryDocumentSnapshot
      | undefined = undefined
  ): Promise<RawMyRoutineUserWithLastDoc> {
    const ref = this.getFetchPreviousRef(isSolved, startAfterDoc);
    const querySnapshot = await ref.get();
    const docs = await querySnapshot.docs;
    const users: RawMyRoutineUser[] = docs.map(doc => {
      const data = doc.data() as RawMyRoutineUser;
      data.id = doc.id;
      return data;
    });

    const lastDocOfNext = docs[0];
    const lastDocOfPrevious = docs[docs.length - 1];

    return {
      lastDocOfNext,
      lastDocOfPrevious,
      users: users.reverse(),
    };
  }

  private static getFetchPreviousRef(
    isSolved: boolean,
    startAfterDoc:
      | firebase.firestore.QueryDocumentSnapshot
      | undefined = undefined
  ) {
    if (startAfterDoc) {
      return this.getQuizResultsRef()
        .where("isSolved", "==", isSolved)
        .orderBy("updatedAt", "asc")
        .startAfter(startAfterDoc)
        .limit(FETCH_USERS_LIMIT);
    }

    return this.getQuizResultsRef()
      .where("isSolved", "==", isSolved)
      .orderBy("updatedAt", "asc")
      .limit(FETCH_USERS_LIMIT);
  }

  public static async searchUsersByPhone(
    phone: string,
    isSolved: boolean
  ): Promise<RawMyRoutineUser[]> {
    const ref = this.getQuizResultsRef()
      .where("isSolved", "==", isSolved)
      .where("phone", "==", phone);

    const querySnapshot = await ref.get();
    const docs = await querySnapshot.docs;
    const users: RawMyRoutineUser[] = docs.map(doc => {
      const data = doc.data() as RawMyRoutineUser;
      data.id = doc.id;
      return data;
    });

    return users;
  }

  public static async updateMyRoutineUserSolvedBy(
    docId: string,
    solverEmail: string
  ) {
    const isSolved = solverEmail !== "";
    const ref = this.getQuizResultsRef();
    return ref.doc(docId).update({
      isSolved,
      solvedBy: solverEmail,
    });
  }

  public static async deleteUser(docId: string) {
    const ref = this.getQuizResultsRef();
    return ref.doc(docId).delete();
  }

  public static async fetchUsersForDownloading(
    isSolved: boolean,
    startDate: number,
    endDate: number
  ): Promise<RawMyRoutineUser[]> {
    const ref = this.getQuizResultsRef()
      .where("isSolved", "==", isSolved)
      .where("updatedAt", ">=", startDate)
      .where("updatedAt", "<=", endDate);
    const querySnapshot = await ref.get();
    const docs = await querySnapshot.docs;

    const users: RawMyRoutineUser[] = docs.map(doc => {
      const data = doc.data() as RawMyRoutineUser;
      data.id = doc.id;
      return data;
    });

    return users;
  }
}
